import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, MapPin, ClipboardCheck, Clock, DollarSign, LogIn,
  AlertTriangle, Download, CheckCircle2, Shield,
  Sparkles, LayoutDashboard, ChevronDown, PlayCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/authContext";
import { useI18n } from "@/lib/i18nContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import LanguageSelector from "@/components/LanguageSelector";
const LandingPreview = lazy(() => import("@/components/landing/LandingPreview"));
const LandingPricing = lazy(() => import("@/components/landing/LandingPricing"));

const featureKeys = [
  { icon: MapPin, titleKey: "features.location.title", descKey: "features.location.desc" },
  { icon: ClipboardCheck, titleKey: "features.permits.title", descKey: "features.permits.desc" },
  { icon: DollarSign, titleKey: "features.budget.title", descKey: "features.budget.desc" },
  { icon: Clock, titleKey: "features.timeline.title", descKey: "features.timeline.desc" },
  { icon: AlertTriangle, titleKey: "features.mistakes.title", descKey: "features.mistakes.desc" },
  { icon: Download, titleKey: "features.export.title", descKey: "features.export.desc" },
];

const howItWorksKeys = [
  { step: "01", titleKey: "how.step1.title", descKey: "how.step1.desc" },
  { step: "02", titleKey: "how.step2.title", descKey: "how.step2.desc" },
  { step: "03", titleKey: "how.step3.title", descKey: "how.step3.desc" },
];

const roadmapBulletKeys = [
  "roadmap.bullet1",
  "roadmap.bullet2",
  "roadmap.bullet3",
  "roadmap.bullet4",
  "roadmap.bullet5",
  "roadmap.bullet6",
];


const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay, ease: [0.21, 0.47, 0.32, 0.98] as const },
});

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();

  const go = (path: string) => () => navigate(path);
  const ctaPath = user ? "/onboarding" : "/auth";

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* ─── NAVBAR ─── */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="container flex items-center justify-between py-4">
          <span className="font-heading text-xl font-medium tracking-tight text-foreground">
            oplif<span className="text-primary">.</span>™
          </span>
          <div className="hidden sm:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#how" className="hover:text-foreground transition-colors duration-200">{t("nav.howItWorks")}</a>
            <a href="#preview" className="hover:text-foreground transition-colors duration-200">{t("nav.preview")}</a>
            <a href="#pricing" className="hover:text-foreground transition-colors duration-200">{t("nav.pricing")}</a>
            <a href="/benchmarks" className="hover:text-foreground transition-colors duration-200">Benchmarks</a>
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-1 hover:text-foreground transition-colors duration-200 outline-none">
                About <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[180px]">
                <DropdownMenuItem onClick={() => navigate("/story")}>Our Story</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/mission")}>Mission & Values</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            {user ? (
              <>
                <Button variant="ghost" size="sm" onClick={go("/dashboard")} className="gap-2 inline-flex text-muted-foreground hover:text-foreground">
                  <LayoutDashboard className="w-4 h-4" /> {t("nav.dashboard")}
                </Button>
                <Button size="sm" onClick={go("/onboarding")} className="shadow-sm hover:shadow-md transition-shadow">{t("nav.goToApp")}</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={go("/demo")} className="gap-2 hidden sm:inline-flex text-muted-foreground hover:text-foreground">
                  <PlayCircle className="w-4 h-4" /> View Demo
                </Button>
                <Button variant="ghost" size="sm" onClick={go("/auth")} className="gap-2 inline-flex text-muted-foreground hover:text-foreground">
                  <LogIn className="w-4 h-4" /> {t("nav.signIn")}
                </Button>
                <Button size="sm" onClick={go("/auth")} className="shadow-sm hover:shadow-md transition-shadow">{t("nav.getStarted")}</Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ─── HERO (deep forest green) ─── */}
      <section className="relative bg-brand-dark text-brand-dark-foreground">
        <div className="container pt-24 sm:pt-32 pb-20 text-center relative">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-gradient-to-b from-accent/20 via-accent/5 to-transparent rounded-full blur-3xl pointer-events-none" />

          <motion.div {...fade()} className="relative">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs font-medium text-white mb-8">
              <Sparkles className="w-3 h-3 text-accent" /> {t("hero.badge")}
            </span>
          </motion.div>
          <motion.h1 {...fade(0.05)}
            className="relative font-heading text-4xl sm:text-5xl md:text-[3.5rem] lg:text-[4rem] font-medium leading-[1.1] tracking-tight text-white max-w-3xl mx-auto"
          >
            {t("hero.stopGuessing")}{" "}<br className="hidden sm:block" /><span className="text-accent">{t("hero.startOpening")}</span>
          </motion.h1>
          <motion.p {...fade(0.1)}
            className="relative mt-6 text-base sm:text-lg text-white/75 max-w-xl mx-auto leading-relaxed"
          >
            {t("hero.subtitle")}
          </motion.p>
          <motion.div {...fade(0.18)} className="relative mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" className="gap-2 text-base px-8 h-12 bg-accent text-accent-foreground hover:bg-accent/90 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200" onClick={go(ctaPath)}>
              {t("hero.createPlan")} <ArrowRight className="w-4 h-4" />
            </Button>
            <Button size="lg" variant="outline" className="text-base px-8 h-12 bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white transition-all duration-200 gap-2" onClick={go("/demo")}>
              <PlayCircle className="w-4 h-4" /> View Demo
            </Button>
          </motion.div>
          <motion.p {...fade(0.25)} className="relative mt-5 text-xs text-white/60">
            {t("hero.freeToStart")}
          </motion.p>
        </div>

        {/* ─── TRUST BAR (inside hero green) ─── */}
        <motion.div {...fade()} className="container pb-20">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[13px] text-white/65">
            <span>{t("hero.saveResearch")}</span>
          </div>
        </motion.div>
      </section>

      {/* ─── LIVE PREVIEW ─── */}
      <section id="preview" className="container pb-32">
        <motion.div {...fade()}>
          <Suspense fallback={<div className="min-h-[400px]" aria-hidden="true" />}>
            <LandingPreview onSignUp={go(ctaPath)} />
          </Suspense>
        </motion.div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section id="how" className="container pb-32">
        <motion.div {...fade()} className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl font-medium text-foreground mb-4">{t("how.title")}</h2>
          <p className="text-muted-foreground max-w-md mx-auto">{t("how.subtitle")}</p>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-8 sm:gap-12">
          {howItWorksKeys.map((s, i) => (
            <motion.div key={s.step} {...fade(0.12 * i)} className="text-center group">
              <div className="font-heading text-6xl font-medium text-primary/10 mb-4 group-hover:text-primary/20 transition-colors duration-300">{s.step}</div>
              <h3 className="font-heading font-medium text-foreground text-lg mb-2">{t(s.titleKey)}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">{t(s.descKey)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section className="container pb-32">
        <motion.div {...fade()} className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl font-medium text-foreground mb-4">{t("features.title")}</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">{t("features.subtitle")}</p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featureKeys.map((f, i) => (
            <motion.div key={f.titleKey} {...fade(0.06 * i)}
              className="group bg-card rounded-xl p-6 border border-border hover:border-primary/20 hover:shadow-elevated transition-all duration-300"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center mb-4 group-hover:bg-primary/12 transition-colors duration-300">
                <f.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-heading font-medium text-foreground text-[15px] mb-2">{t(f.titleKey)}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{t(f.descKey)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── AI ROADMAP ─── */}
      <section className="container pb-32">
        <motion.div {...fade()}
          className="relative bg-card rounded-2xl border border-border p-8 sm:p-14 overflow-hidden"
        >
          {/* Subtle accent glow */}
          <div className="absolute -top-20 -right-20 w-60 h-60 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

          <div className="relative">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-medium text-foreground">{t("roadmap.title")}</h2>
            </div>
            <p className="text-muted-foreground max-w-2xl mb-10 leading-relaxed">
              {t("roadmap.desc")}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
              {roadmapBulletKeys.map((key) => (
                <div key={key} className="flex items-center gap-2.5 group">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0 group-hover:scale-110 transition-transform duration-200" />
                  <span className="text-sm text-foreground font-medium">{t(key)}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>


      {/* ─── PRICING ─── */}
      <section id="pricing" className="container pb-32">
        <Suspense fallback={<div className="min-h-[400px]" aria-hidden="true" />}>
          <LandingPricing onGetStarted={go(ctaPath)} />
        </Suspense>
      </section>

      {/* ─── FINAL CTA (deep forest green) ─── */}
      <section className="bg-brand-dark text-brand-dark-foreground py-20 sm:py-28">
        <div className="container">
          <motion.div {...fade()} className="relative max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-white mb-4">{t("cta.title")}</h2>
            <p className="text-white/75 mb-8 max-w-md mx-auto">
              {t("cta.subtitle")}
            </p>
            <Button size="lg" className="gap-2 h-12 px-8 bg-accent text-accent-foreground hover:bg-accent/90 shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-200" onClick={go(ctaPath)}>
              {t("cta.button")} <ArrowRight className="w-4 h-4" />
            </Button>
            <p className="mt-4 text-xs text-white/60">{t("cta.free")}</p>
          </motion.div>
        </div>
      </section>

      {/* ─── FOOTER (deep forest green) ─── */}
      <footer className="bg-brand-dark text-brand-dark-foreground">
        <div className="container py-12">
          {/* Top row: brand + nav columns */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <span className="font-heading text-base font-medium text-white tracking-tight">oplif<span className="text-accent">.</span>™</span>
              <p className="mt-2 text-xs text-white/60 leading-relaxed">
                {t("footer.tagline")}
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-xs font-medium text-white uppercase tracking-wider mb-3">{t("footer.product")}</h4>
              <ul className="space-y-2 text-[13px] text-white/65">
                <li><a href="#how" className="hover:text-accent transition-colors">{t("nav.howItWorks")}</a></li>
                <li><a href="#pricing" className="hover:text-accent transition-colors">{t("nav.pricing")}</a></li>
                <li><button onClick={() => navigate("/plan")} className="hover:text-accent transition-colors">{t("nav.preview")}</button></li>
                <li><a href="/benchmarks" className="hover:text-accent transition-colors">Benchmarks</a></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-xs font-medium text-white uppercase tracking-wider mb-3">Company</h4>
              <ul className="space-y-2 text-[13px] text-white/65">
                <li><a href="/story" className="hover:text-accent transition-colors">Our Story</a></li>
                <li><a href="/mission" className="hover:text-accent transition-colors">Mission & Values</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-xs font-medium text-white uppercase tracking-wider mb-3">{t("footer.legal")}</h4>
              <ul className="space-y-2 text-[13px] text-white/65">
                <li>
                  <a
                    href="https://www.iubenda.com/privacy-policy/50445777"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-accent transition-colors"
                  >
                    {t("footer.privacy")}
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.iubenda.com/privacy-policy/50445777/cookie-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-accent transition-colors"
                  >
                    Cookie Policy
                  </a>
                </li>
                <li>
                  <a href="/terms" className="hover:text-accent transition-colors">
                    {t("footer.terms")}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Social icons */}
          <div className="border-t border-white/15 pt-6 flex justify-center items-center gap-5 mb-4">
            <a
              href="https://instagram.com/oplifapp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-[#888888] hover:text-[#2d8653] transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </a>
            <a
              href="https://tiktok.com/@oplifapp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="text-[#888888] hover:text-[#2d8653] transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52V6.91a4.85 4.85 0 0 1-1.84-.22z" />
              </svg>
            </a>
            <a
              href="https://linkedin.com/company/oplif"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="text-[#888888] hover:text-[#2d8653] transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
              </svg>
            </a>
          </div>

          {/* Bottom bar */}
          <div className="text-xs text-white/60 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <span>{t("footer.copyright")}</span>
            <button
              type="button"
              onClick={() => {
                // Open iubenda Cookie Solution preferences if available
                const w = window as unknown as { _iub?: { cs?: { api?: { openPreferences?: () => void } } } };
                w._iub?.cs?.api?.openPreferences?.();
              }}
              className="text-white/40 hover:text-white/70 transition-colors underline-offset-2 hover:underline"
            >
              Your Privacy Choices
            </button>
            <a
              href="https://www.iubenda.com/privacy-policy/50445777"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/40 hover:text-white/70 transition-colors underline-offset-2 hover:underline"
            >
              Notice at Collection
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
